import type { ImportRowError } from "@adva/types";

export interface ImportErrorGroup {
  /** Common error message that defines the group. */
  message: string;
  /** Affected per-row errors, in submission order. */
  rows: ImportRowError[];
  /**
   * Number of DISTINCT non-negative `row` values in this group — i.e. how many
   * CSV rows are affected. NOT `rows.length`: validation can append multiple
   * errors for the same row (e.g. two invalid fields share one message), so
   * counting error objects over-reports the affected-row total the UI renders
   * as "N rows". Also the sort key.
   */
  count: number;
}

// ADV-1017: group per-row errors by exact `message` string.
//
// - Excludes aggregate sentinel entries (`row < 0`); callers display
//   those separately.
// - Output is ordered by descending count, then by first-seen index
//   (stable for ties — the message that appeared first wins).
export function groupErrorsByMessage(
  errors: readonly ImportRowError[],
): ImportErrorGroup[] {
  const byMessage = new Map<string, ImportErrorGroup>();
  const firstSeen = new Map<string, number>();
  // ADV-1017: track the DISTINCT non-negative `row` values per message so
  // `count` reflects affected rows, not the number of ImportRowError objects.
  const distinctRows = new Map<string, Set<number>>();

  errors.forEach((entry, i) => {
    if (entry.row < 0) return;
    let group = byMessage.get(entry.message);
    let rows = distinctRows.get(entry.message);
    if (!group || !rows) {
      group = { message: entry.message, rows: [], count: 0 };
      rows = new Set<number>();
      byMessage.set(entry.message, group);
      distinctRows.set(entry.message, rows);
      firstSeen.set(entry.message, i);
    }
    group.rows.push(entry);
    rows.add(entry.row);
    group.count = rows.size;
  });

  return [...byMessage.values()].sort((a, b) => {
    if (a.count !== b.count) return b.count - a.count;
    return (firstSeen.get(a.message) ?? 0) - (firstSeen.get(b.message) ?? 0);
  });
}
